import React, { useEffect, useRef, useState } from "react"
import { useStaticQuery, graphql } from "gatsby"
import { Disclosure, DisclosurePanel, DisclosureButton, Menu, MenuItems, MenuButton, Popover, PopoverButton, PopoverPanel, PopoverGroup, Transition } from "@headlessui/react"
import { XMarkIcon } from "@heroicons/react/24/outline"
import { flatListToHierarchical } from "../utils"
import MenuIcon from "./icons/menu"
import Language from "./icons/language"
import Chevron from "./icons/chevron"
import Arrow from "./icons/arrow"
import MyLink from "./my-link"
import { FlapBottom, FlapIcon, FlapTop } from "./icons/fly-room/flap"

export default function Navbar({ uri }) {
  const data = useStaticQuery(graphql`
    {
      wpMenu(name: { eq: "Header" }) {
        menuItems {
          nodes {
            id
            title: label
            uri
            target
            parent: parentId
            cssClasses
            description
            headerMenu {
              menuItemIcon {
                altText
                localFile {
                  publicURL
                }
              }
            }
          }
        }
      }
      wpOption(id: { eq: "cG9zdDo5OA==" }) {
        header {
          header {
            logo {
              altText
              localFile {
                publicURL
              }
            }
            mobileLogo {
              altText
              localFile {
                publicURL
              }
            }
            cta {
              title
              url
              target
            }
          }
        }
      }
    }
  `)
  const { logo, mobileLogo, cta } = data?.wpOption?.header?.header
  const routes = flatListToHierarchical(data?.wpMenu?.menuItems?.nodes, {
    idKey: "id",
    childrenKey: "routes",
    parentKey: "parent",
  })
  const routesLength = routes.length

  const [scrollDir, setScrollDir] = useState("at top")

  useEffect(() => {
    const threshold = 0
    let lastScrollY = window.pageYOffset
    let ticking = false

    const updateScrollDir = () => {
      const scrollY = window.pageYOffset

      if (Math.abs(scrollY - lastScrollY) < threshold) {
        ticking = false
        return
      }
      setScrollDir(
        scrollY === 0
          ? "at top"
          : scrollY > lastScrollY
            ? "scrolling down"
            : "scrolling up"
      )
      lastScrollY = scrollY > 0 ? scrollY : 0
      ticking = false
    }

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateScrollDir)
        ticking = true
      }
    }

    window.addEventListener("scroll", onScroll)

    return () => window.removeEventListener("scroll", onScroll)
  }, [scrollDir])
  const [openDisclosure, setOpenDisclosure] = useState(-1)

  const toggleDisclosure = (id) => {
    setOpenDisclosure((prevState) => {
      if (prevState === id) {
        return -1
      } else {
        return id
      }
    });
  };



  return (
    <div className="bg-white sticky top-0 z-30 shadow-spread-sm leading-tight print:hidden">
      <a href="#main-content" className="sr-only">
        Skip to main content
      </a>
      <Menu>
        {({ open }) => (
          <>

            <div className={`xl:container mx-auto px-8 ${open ? "border-b border-b-slate" : ""} lg:border-b-0`}>
              <div className={`flex justify-between space-x-3 sm:space-x-0  items-center print:pt-0 py-2 lg:py-0`}>
                {(logo || mobileLogo) && (
                  <MyLink
                    to="/"
                    aria-label="home"
                    className="lg:flex-shrink-0 flex items-center mr-6"
                  >
                    {logo && <img
                      className={`hidden lg:block w-28 h-auto transition-all hover-grow ${scrollDir === "at top"
                        ? "hover-grow"
                        : "scale-75 -my-2 hover-grow-85"
                        }`}
                      src={logo.localFile?.publicURL}
                      alt={logo.altText}
                      loading="eager"
                    />}
                    {mobileLogo && <img
                      className={`block lg:hidden h-10 w-auto transition-all hover-grow`}
                      src={mobileLogo.localFile?.publicURL}
                      alt={mobileLogo.altText}
                      loading="eager"
                    />}
                  </MyLink>
                )}
                <nav className="hidden lg:flex flex-grow print:hidden max-w-6xl text-sm xl:text-base xl:leading-tight" aria-label="main navigation">
                  <PopoverGroup className="w-full">
                    <ul className="w-full flex items-center flex-grow space-x-1 h-full justify-between font-medium leading-tight">

                      {routesLength ? (
                        <>
                          {routes.map((menuItem) => {
                            return <>{MenuItem(menuItem, uri, scrollDir)}</>
                          })}
                        </>
                      ) : null}

                      <li className="inline-flex items-center">
                        <MyLink
                          className="text-body hover:text-green focus:text-green hover-grow border-b border-transparent "
                          to="#translate"
                          title="Translate"
                        >
                          <span className="sr-only">
                            Translate
                          </span>
                          <Language aria-hidden className="w-6 lg:w-8" />
                        </MyLink>
                      </li>
                      {cta ? <li className="inline-flex items-center">
                        <MyLink
                          to={cta.url}
                          target={cta.target}
                          className={`btn-sm btn-primary lg:px-3.5 xl:px-5`}
                        >
                          {cta.title}
                        </MyLink>
                      </li> : null}

                    </ul>
                  </PopoverGroup>
                </nav>

                <div className="flex items-center lg:hidden print:hidden space-x-4">
                  {/* Mobile menu button */}
                  <MenuButton className="transition inline-flex items-center -mr-3 justify-center p-3 text-wire hover:bg-green/10 rounded hover:text-green focus:outline-none focus:ring-2 focus:ring-inset focus:ring-bright-blue">
                    <span className="sr-only">Open main menu</span>
                    {open ? (
                      <XMarkIcon className="block w-6" strokeWidth={3} aria-hidden="true" />
                    ) : (
                      <MenuIcon className="block w-[1.375rem] h-[1.375rem]" aria-hidden="true" />
                    )}
                  </MenuButton>
                </div>
              </div>
            </div>

            <MenuItems
              unmount={false}
              className="h-screen lg:hidden absolute top-full w-full inset-x-0 bg-white overflow-y-auto">
              <div className="mx-auto pb-44">

                <ul className="divide-y divide-slate/25">
                  {routesLength ? (
                    <>
                      {routes.map((menuItem, index) => {
                        const submenuItems = menuItem.routes
                        const isOpen = openDisclosure === index
                        return (
                          <>
                            <li key={"menu-" + index} className="px-8">
                              {menuItem.routes?.length ?
                                <>
                                  <Disclosure>
                                    {({ open }) => (
                                      <>

                                        <DisclosureButton
                                          onClick={() => toggleDisclosure(index)}
                                          className={`flex items-stretch justify-between w-full -ml-4 border-l-2 border-l-transparent pl-4 ${menuItem.title === "Hotel" || menuItem.title === "Spa"
                                            ? "text-ruby hover:text-ruby hover:border-ruby focus:text-ruby focus:border-ruby"
                                            : "text-body hover:text-green hover:border-green focus:text-green focus:border-green"
                                            }`}>
                                          <div
                                            className={` text-lg font-semibold leading-tight text-left flex-grow ${menuItem.cssClasses
                                              ? menuItem.cssClasses
                                                .toString()
                                                .replace(/,/g, " ")
                                              : ""
                                              } ${uri !== "/" && uri === menuItem.uri
                                                ? "font-bold"
                                                : ""
                                              }`}
                                          >
                                            <div className="py-4 flex items-center gap-2">
                                              <span dangerouslySetInnerHTML={{ __html: menuItem.title, }} />
                                              {menuItem.title === "Hotel" ? <span className="font-bold leading-none inline-block px-1 py-0.5 uppercase text-xs bg-ruby text-white group-hover:bg-ruby-600 rounded-sm">New!</span> : null}
                                            </div>
                                          </div>

                                          <div className='py-4 px-6 justify-self-stretch -mr-8 text-green inline-block flex-shrink-0'>
                                            <Chevron className={`w-3 transition ${isOpen ? "-rotate-90" : "rotate-90"}`} strokeWidth={2} />
                                          </div>
                                        </DisclosureButton>

                                        <DisclosurePanel static="true" className={`border-t border-t-slate/25 -mx-8 ${isOpen ? "" : "hidden"}`}>
                                          <>

                                            <ul className="divide-y divide-light-gray px-8">
                                              {submenuItems.map(subMenuItem => {
                                                return (
                                                  <li key={subMenuItem.title}>

                                                    <Menu.Item
                                                      as={MyLink}
                                                      className={`py-3 leading-tight block px-4 hover:no-underline text-body hover:text-green focus:text-green hover:border-l-green focus:border-l-green border-l-2 border-l-transparent  ${subMenuItem.cssClasses
                                                        ? subMenuItem.cssClasses
                                                          .toString()
                                                          .replace(",", " ")
                                                        : ""
                                                        }
                                                      ${uri !== "/" && uri === subMenuItem.uri
                                                          ? "font-bold"
                                                          : ""
                                                        }`}
                                                      target={subMenuItem.target}
                                                      to={subMenuItem.uri}>
                                                      <span
                                                        dangerouslySetInnerHTML={{
                                                          __html: subMenuItem.title
                                                        }} />
                                                      {subMenuItem.arrow ? <Arrow className="inline-block" /> : null}
                                                    </Menu.Item>

                                                    {subMenuItem.routes?.length ? (
                                                      <ul className="ml-6 mb-2">
                                                        {subMenuItem.routes.map(subSubMenuItem => {
                                                          return (
                                                            <li key={subSubMenuItem.title}>

                                                              <Menu.Item
                                                                as={MyLink}
                                                                className={`pl-3 hover:-ml-1 border-l hover:border-l-[5px] border-t-slate/25 hover:border-l-green py-2 leading-tight block link-body ${subSubMenuItem.cssClasses
                                                                  ? subSubMenuItem.cssClasses
                                                                    .toString()
                                                                    .replace(",", " ")
                                                                  : ""
                                                                  }`}
                                                                target={subSubMenuItem.target}
                                                                to={subSubMenuItem.uri}
                                                              >
                                                                {subSubMenuItem.title}
                                                              </Menu.Item>

                                                            </li>
                                                          )
                                                        })}
                                                      </ul>
                                                    ) : null}
                                                  </li>
                                                )
                                              })}
                                              {menuItem.title === "Hotel" ? <li className="flex justify-end -mx-8"><MyLink to={'/restaurant/fly-room/'} className=" -mt-4 h-16 w-24 group relative block">
                                                <FlapBottom className="group-hover:scale-125 transition group-hover:opacity-70 origin-bottom-right absolute bottom-0 right-0 w-full" />
                                                <FlapIcon className="group-hover:animate-wiggle transform transition origin-center absolute bottom-0 right-0 w-full overflow-hidden" />
                                                <FlapTop className="group-hover:scale-125 transition origin-bottom-right absolute bottom-0 right-0" />
                                              </MyLink></li> : null}

                                            </ul>
                                          </>
                                        </DisclosurePanel>
                                      </>
                                    )}
                                  </Disclosure>
                                </>
                                :
                                <Menu.Item
                                  to={menuItem.uri}
                                  target={menuItem.target}
                                  as={MyLink}
                                  className={`text-body hover:text-green hover:border-green focus:text-green focus:border-green text-lg font-semibold py-4 leading-tight flex gap-2 items-center -ml-4  border-l-2 border-l-transparent pl-4 ${menuItem.cssClasses
                                    ? menuItem.cssClasses
                                      .toString()
                                      .replace(/,/g, " ")
                                    : ""
                                    } ${uri !== "/" && uri === menuItem.uri
                                      ? "font-bold"
                                      : ""
                                    }
                              `}>
                                  <span dangerouslySetInnerHTML={{
                                    __html: menuItem.title,
                                  }} />

                                </Menu.Item>
                              }

                            </li>
                          </>
                        )
                      })}
                    </>
                  ) : null}
                  <li className="py-3 px-8">
                    <Menu.Item as={MyLink}
                      className="link-body flex justify-between text-lg font-semibold"
                      to="#translate"

                    >
                      Translate <Language aria-hidden className="w-8 inline-block" />
                    </Menu.Item>
                  </li>

                  {cta ? <li className="py-4 px-8 border-b border-transparent"><Menu.Item
                    to={cta.url}
                    target={cta.target}
                    as={MyLink}
                    className={`btn-sm btn-primary`}
                  >
                    {cta.title}
                  </Menu.Item></li> : null}
                </ul>

              </div>
            </MenuItems>

          </>
        )}
      </Menu>
    </div>
  )
}

function MenuItem(menuItem, uri, scrollDir) {
  let timeout // NodeJS.Timeout
  const timeoutDuration = 10

  const buttonRef = useRef(null) // useRef<HTMLButtonElement>(null)
  const [openState, setOpenState] = useState(true) // eslint-disable-line

  const toggleMenu = open => { // eslint-disable-line
    // log the current open state in React (toggle open state)
    setOpenState(openState => !openState)
    // toggle the menu by clicking on buttonRef
    buttonRef?.current?.click() // eslint-disable-line
  }

  // Open the menu after a delay of timeoutDuration
  const onHover = (open, action) => {
    // if the modal is currently closed, we need to open it
    // OR
    // if the modal is currently open, we need to close it
    if (
      (!open && action === "onMouseEnter") ||
      (open && action === "onMouseLeave")
    ) {
      // clear the old timeout, if any
      clearTimeout(timeout)
      // open the modal after a timeout
      timeout = setTimeout(() => toggleMenu(open), timeoutDuration)
    }
    // else: don't click! 😁
  }

  const handleClickOutside = event => {
    if (buttonRef.current && !buttonRef.current.contains(event.target)) {
      event.stopPropagation()
    }
  }
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside)

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  })

  return (
    <li
      key={menuItem.title}
      className={`flex items-center mx-2`}
    >
      {menuItem.routes?.length ? (
        <nav aria-label="main navigation">
          <Popover className="static"

          >
            {({ open }) => (
              <button
                onMouseLeave={() => onHover(open, "onMouseLeave")}
                aria-labelledby={`menu-item-${menuItem.title}`}
              >
                <PopoverButton
                  onMouseEnter={() => onHover(open, "onMouseEnter")}
                  onKeyDown={() => onHover(open, "onMouseEnter")}
                  ref={buttonRef}
                  className={`hover:outline-none px-6 -mx-6 focus:outline-none transition-[padding] group/parent cursor-auto ${scrollDir === "at top"
                    ? "py-8"
                    : "py-4"
                    }`}
                >
                  <span
                    className={`border-b  inline-flex flex-shrink-0 items-center relative py-2.5 ${menuItem.cssClasses
                      ? menuItem.cssClasses.toString().replace(",", " ")
                      : ""
                      } ${isCurrentPage(menuItem, uri)
                        ? (menuItem.title === "Hotel" || menuItem.title === "Spa")
                          ? "border-b-ruby"
                          : "border-b-body"
                        : "border-b-transparent"
                      } ${menuItem.title === "Hotel" || menuItem.title === "Spa"
                        ? "text-ruby hover:border-b-ruby group-hover/parent:border-b-ruby focus:border-b-ruby"
                        : "text-body link-hover-body hover:border-b-body group-hover/parent:border-b-body focus:border-b-body"
                      } ${menuItem.uri === "#" ? "hover:border-opacity-50 group-hover/parent:border-opacity-50" : ""}`}
                  >
                    {menuItem.title === "Hotel" ? <span className="absolute left-1/2 transform -translate-x-1/2 -top-3 font-bold leading-none inline-block px-1 py-0.5 uppercase text-xs bg-ruby text-white group-hover/parent:opacity-50 rounded-sm">New!</span> : null}
                    <span className="cursor-default hover:opacity-50 group-hover/parent:opacity-50" id={`menu-item-${menuItem.title}`} dangerouslySetInnerHTML={{
                      __html: menuItem.title,
                    }} />

                  </span>
                </PopoverButton>


                <Transition
                  show={open}
                  unmount={false}
                  // show={menuItem.title === "Hotel"}
                  enter="transition ease-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="transition ease-in duration-150"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <PopoverPanel unmount={false} className="text-left absolute inset-x-0 z-10 top-full transform bg-menu-light shadow-spread-down max-h-[calc(100vh_-_7rem)] overflow-y-auto">

                    <div className="xl:container pt-8 pb-16 px-8 mx-auto">
                      <div className="">
                        <div className="sr-only" dangerouslySetInnerHTML={{
                          __html: menuItem.title,
                        }} />
                        <Transition
                          show={open}
                          unmount={false}
                          // show={menuItem.title === "Hotel"}
                          enter="transition-all ease-out duration-300"
                          enterFrom="-translate-x-8"
                          enterTo="translate-x-0"
                          leave="transition ease-in duration-300"
                          leaveFrom="translate-x-0"
                          leaveTo="-translate-x-8"
                        >
                          <ul
                            className={`grid grid-cols-3 gap-x-2 gap-y-4`}
                          >
                            {menuItem.routes.map((subMenuItem) => {
                              const { title, uri, target, description, cssClasses, } = subMenuItem
                              const icon = subMenuItem?.headerMenu?.menuItemIcon

                              return (
                                <li key={title} className="max-w-md">
                                  <MyLink
                                    className={`py-5 relative z-10 link-hover-body group leading-tight flex items-center px-5 hover:bg-green/5 hover:no-underline  ${cssClasses
                                      ? cssClasses
                                        .toString()
                                        .replace(",", " ")
                                      : ""
                                      }`}
                                    target={target}
                                    to={uri}
                                  >
                                    <div className="w-16 mr-3 flex-shrink-0">{icon ?
                                      <img
                                        className={`hover-grow w-16 inline-block`}
                                        src={icon.localFile?.publicURL}
                                        alt={icon.altText}
                                        loading="eager"
                                        aria-hidden
                                      /> : null}</div>
                                    <div>
                                      {title ? <div className="h3 leading-tight" dangerouslySetInnerHTML={{
                                        __html: title,
                                      }} /> : null}
                                      {description ? <div className="w-full font-normal mt-1" dangerouslySetInnerHTML={{
                                        __html: description,
                                      }} /> : null}
                                    </div>
                                  </MyLink>

                                  {subMenuItem.routes?.length ? (
                                    <ul className="pl-24 ml-0.5 cursor-auto">
                                      <>
                                        {subMenuItem.routes.map(subSubMenuItem => {
                                          return (
                                            <li key={subSubMenuItem.title}>

                                              <MyLink
                                                className={`pl-3 border-l-4 border-gray-800/10 hover:border-green py-2 link-body block ${subSubMenuItem.cssClasses
                                                  ? subSubMenuItem.cssClasses
                                                    .toString()
                                                    .replace(",", " ")
                                                  : ""
                                                  }`}
                                                target={subSubMenuItem.target}
                                                to={subSubMenuItem.uri}
                                              >
                                                {subSubMenuItem.title}
                                              </MyLink>

                                            </li>
                                          )
                                        })}
                                      </>
                                    </ul>
                                  ) : null}

                                </li>
                              )
                            })}
                          </ul>
                        </Transition>
                      </div>
                    </div>
                    {menuItem.title === "Hotel" ? <MyLink to={'/restaurant/fly-room/'} className="absolute bottom-0 right-0 w-32 h-[5.625rem] group">
                      <FlapBottom className="group-hover:scale-125 transition group-hover:opacity-70  origin-bottom-right absolute bottom-0 right-0 w-full" />
                      <FlapIcon className="group-hover:animate-wiggle transform transition origin-center absolute bottom-0 right-0 w-full overflow-hidden" />
                      <FlapTop className="group-hover:scale-125 transition origin-bottom-right absolute bottom-0 right-0" />
                    </MyLink> : null}
                  </PopoverPanel>
                </Transition>

              </button>
            )}
          </Popover>

        </nav>
      ) :
        <MyLink
          to={menuItem.uri}
          target={menuItem.target}
          className={`border-y border-transparent inline-flex flex-shrink-0 items-center relative py-2.5 
          ${menuItem.cssClasses
              ? menuItem.cssClasses.toString().replace(",", " ")
              : ""
            }
          ${isCurrentPage(menuItem, uri)
              ? "border-b-body"
              : "border-b-transparent"
            }
            text-body hover:text-body focus:text-body hover:border-b-body focus:border-b-body
            `}
        >
          {menuItem.title}
        </MyLink>
      }
    </li>
  )
}

function isCurrentPage(menuItem, currentPageUri) {
  return (currentPageUri !== "/" && typeof currentPageUri === 'string'
    && (currentPageUri?.includes(menuItem.uri)))
}